import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Footer from '../components/Footer';

interface Device {
  id: string;
  name: string;
  icon: string;
}

const VALID_DEVICE_IDS = ['ez-care', 'ez-care-plus', 'lifebeacon', 'other'];

const TroubleshootingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const deviceFromUrl = searchParams.get('device');
  const initialDevice = deviceFromUrl && VALID_DEVICE_IDS.includes(deviceFromUrl) ? deviceFromUrl : null;
  const [selectedDevice, setSelectedDevice] = useState<string | null>(initialDevice);

  useEffect(() => {
    if (deviceFromUrl && VALID_DEVICE_IDS.includes(deviceFromUrl)) {
      setSelectedDevice(deviceFromUrl);
    }
  }, [deviceFromUrl]);

  const handleDeviceSelect = (deviceId: string) => {
    setSelectedDevice(deviceId);
    setSearchParams({ device: deviceId }, { replace: true });
  };

  const devices: Device[] = [
    { id: 'ez-care', name: 'EZ Care', icon: '🏠' },
    { id: 'ez-care-plus', name: 'EZ Care Plus', icon: '🏠+' },
    { id: 'lifebeacon', name: 'LifeBeacon', icon: '📍' },
    { id: 'other', name: 'Other / Not Sure', icon: '❓' }
  ];

  const troubleshootingInfo: { [key: string]: any } = {
    'ez-care': {
      title: 'EZ Care In-Home Medical Alert',
      issues: [
        {
          problem: 'Device not powering on',
          solution: 'Check that the power cord is firmly plugged into both the device and wall outlet. Try a different outlet. If still not working, call 1-877-579-9862.'
        },
        {
          problem: 'Lights flashing red',
          solution: 'This indicates a problem with the phone connection. Check that the phone line is properly connected to both the device and wall jack. Test your phone line to ensure it has a dial tone.'
        },
        {
          problem: 'Button not responding',
          solution: 'Try pressing and holding the button for 3-5 seconds. Check the battery in the help button - it may need replacement. Press the test button on the base unit to verify the system is working.'
        },
        {
          problem: 'Cannot hear operator clearly',
          solution: 'Ensure the volume on the base unit is turned up. Move closer to the base unit. Check for any objects blocking the speaker. If issue persists, call technical support at 1-877-579-9862 ext 3.'
        }
      ]
    },
    'ez-care-plus': {
      title: 'EZ Care Plus In-Home Medical Alert (Cellular)',
      issues: [
        {
          problem: 'No cellular signal',
          solution: 'Move the base unit to a location with better cell reception, preferably near a window. The device needs AT&T or T-Mobile coverage to function properly.'
        },
        {
          problem: 'Lights flashing yellow',
          solution: 'This indicates low cellular signal. Relocate the base unit to improve reception. If signal doesn\'t improve, call 1-877-579-9862.'
        },
        {
          problem: 'Device not charging',
          solution: 'Ensure the power adapter is fully plugged in. Check the charging contacts are clean. Try a different outlet. Battery may need replacement - call 1-877-579-9862 ext 3.'
        }
      ]
    },
    'lifebeacon': {
      title: 'LifeBeacon On-the-Go GPS Medical Alert',
      issues: [
        {
          problem: 'GPS not working',
          solution: 'Ensure you\'re outdoors or near a window for best GPS signal. Allow 30-60 seconds for GPS to lock on. The device needs clear view of the sky to get location.'
        },
        {
          problem: 'Battery drains quickly',
          solution: 'Ensure the device is fully charged (charge overnight). Avoid extreme temperatures. GPS usage drains battery faster. Turn off device when not in use to conserve battery.'
        },
        {
          problem: 'Button press not connecting',
          solution: 'Check that you\'re in an area with cellular coverage. Ensure device is charged (green light). Try moving to an open area. Call 1-877-579-9862 if issue persists.'
        }
      ]
    },
    'other': {
      title: 'General Troubleshooting',
      issues: [
        {
          problem: 'Not sure which system I have',
          solution: 'Check the label on the back of your device for the model name. Look at your invoice or packing slip. Call Member Care at 1-877-579-9862 ext 2 and they can help identify your system.'
        },
        {
          problem: 'Need to test my system',
          solution: 'Press your help button and wait for an operator to answer. Tell them you are testing your system. They will verify everything is working properly. We recommend testing monthly.'
        },
        {
          problem: 'Lost my instruction manual',
          solution: 'Visit our How It Works page for general instructions. Call Member Care at 1-877-579-9862 ext 2 and we can email or mail you a replacement manual.'
        }
      ]
    }
  };

  return (
    <div className="troubleshooting-page">
      <section className="troubleshooting-content">
        <div className="container">
          <h1 className="troubleshooting-title">Troubleshooting Your Medical Alert System</h1>
          <p className="troubleshooting-subtitle">
            Select your device to see common issues and solutions
          </p>

          {/* Device Selection Grid */}
          <div className="device-grid-compact">
            {devices.map((device) => (
              <button
                key={device.id}
                className={`device-tile-compact ${selectedDevice === device.id ? 'selected' : ''}`}
                onClick={() => handleDeviceSelect(device.id)}
              >
                <span className="device-icon-compact">{device.icon}</span>
                <span className="device-name-compact">{device.name}</span>
              </button>
            ))}
          </div>

          {/* Troubleshooting Content */}
          {selectedDevice && troubleshootingInfo[selectedDevice] && (
            <div className="troubleshooting-results">
              <h2>{troubleshootingInfo[selectedDevice].title}</h2>
              <div className="issues-list">
                {troubleshootingInfo[selectedDevice].issues.map((issue: any, index: number) => (
                  <div key={index} className="issue-card">
                    <h3 className="issue-problem">Problem: {issue.problem}</h3>
                    <p className="issue-solution"><strong>Solution:</strong> {issue.solution}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Help Section */}
          <div className="troubleshooting-help">
            <h3>Still Need Help?</h3>
            <p>
              Our technical support team is available 24/7 to assist you with any issues.
            </p>
            <div className="help-options">
              <a href="tel:1-877-579-9862" className="btn btn-primary">
                Call Technical Support: 1-877-579-9862 ext 3
              </a>
              <Link to="/faq" className="btn btn-secondary">
                View FAQ
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TroubleshootingPage;

